module.exports = ({
	ViewIdentifier: 'HeadlightPanels',
	DefaultRenderable: 'HeadlightPanels',
	DefaultDestinationAddress: '#HeadlightPanels-Container',
	CSS: /*css*/`
		#HeadlightPanels-Navbar-Brand-Container {
			display: flex;
		}

		/* Auto-save indicator — sits immediately left of the navbar Save button. Quiet by design:
		   it is ambient status, not a notification. */
		.hl-autosave-status-container {
			display: inline-flex;
			align-items: center;
			margin-right: 0.5rem;
		}
		/* Below Bulma's desktop breakpoint .navbar-item is display: block, which drops the status onto the text baseline. */
		.navbar-item:has(> .hl-autosave-status-container) {
			display: flex;
			align-items: center;
		}
		.hl-autosave-status {
			display: inline-flex;
			align-items: center;
			gap: 0.25rem;
			font-size: 0.75rem;
			line-height: 1;
			white-space: nowrap;
			opacity: 0.65;
			transition: opacity 200ms ease-in-out;
		}
		.hl-autosave-status .icon.is-small {
			font-size: 0.75rem;
		}
		.hl-autosave-status.is-retrying,
		.hl-autosave-status.is-suspended,
		.hl-autosave-status.is-offline {
			opacity: 1;
		}
		.hl-autosave-status.is-saved.is-flashing {
			animation: hl-autosave-flash 2s ease-in-out 1;
		}
		@keyframes hl-autosave-flash {
			0% { opacity: 1; }
			40% { opacity: 1; }
			100% { opacity: 0.65; }
		}
		@media (prefers-reduced-motion: reduce) {
			.hl-autosave-status.is-saved.is-flashing { animation: none; }
		}
		/* Auto-save preferences in the Settings panel. The seconds field is fixed-width and the row wraps
		   rather than growing, so opening this panel never changes its width. */
		.hl-autosave-interval {
			display: flex;
			align-items: center;
			flex-wrap: wrap;
			gap: 0.4rem;
			font-size: 0.85rem;
			min-width: 0;
		}
		.hl-autosave-interval-input {
			flex: 0 0 auto;
			width: 5.5rem;
		}
		.hl-autosave-note {
			font-size: 0.8rem;
			opacity: 0.7;
		}
		/* Approval actions are inert while a save (manual or auto) is in flight. pointer-events is what
		   actually blocks the click — the handler is bound with onclick, not a disabled attribute.
		   The same treatment covers a Settings row the form manifest has taken out of the user's hands. */
		.panel-block.is-disabled,
		a.panel-block.is-disabled {
			pointer-events: none;
			cursor: not-allowed;
			opacity: 0.5;
		}
		/* An approval action is in flight. The form stays readable on purpose — the user should still be
		   able to see what they just submitted — so this dims and de-fangs rather than covering with a
		   scrim. Scoped to the body because Select2 and flatpickr portal their popups outside the form.
		   The inert attribute is what actually blocks the keyboard; pointer-events only stops the mouse. */
		body.hl-approval-busy {
			cursor: progress;
		}
		body.hl-approval-busy #Headlight-Document-Form {
			pointer-events: none;
			opacity: 0.55;
			transition: opacity 150ms ease-in-out;
		}
		body.hl-approval-busy .select2-container--open,
		body.hl-approval-busy .flatpickr-calendar.open {
			display: none;
		}
		@media (prefers-reduced-motion: reduce) {
			body.hl-approval-busy #Headlight-Document-Form { transition: none; }
		}
		`,
	Templates:
		[
			{
				Hash: 'HeadlightPanels-Template',
				Template: /*html*/`
				<div id="HeadlightPanels-LeftPanel" class="hl-panel-wrapper hl-panel-wrapper-left animate__animated animate__faster"></div>
				<div id="HeadlightPanels-RightPanel" class="hl-panel-wrapper hl-panel-wrapper-right animate__animated animate__faster"></div>
				<nav id="HeadlightPanels-NavBar" class="navbar is-flex is-fixed-{~D:Pict.settings.HeadlightPanels.Configuration.NavBar.location~} {~D:Pict.settings.HeadlightPanels.Configuration.NavBar.colorClass~}">
					<span id="HeadlightPanels-Navbar-Brand-Container">{~T:HeadlightPanels-NavBar-Brand~}</span>
					<div id="HeadlightPanels-Navbar-Button-Container" class="navbar-end">{~T:HeadlightPanels-NavBar-Button-Wrapper~}</div>
				</nav>
				<aside id="HeadlightPanels-FullScreenModal-Container"></aside>`,
			},
			{
				'Hash': 'HeadlightPanels-NavBar-Brand',
				'Template': /*html*/`
				<div class="navbar-brand {~D:Pict.settings.HeadlightPanels.Configuration.NavBar.Brand.wrapperClass~}">
					<button 
						id="{~D:Pict.settings.HeadlightPanels.Configuration.NavBar.Brand.id~}" 
						type="button" 
						class="button {~D:Pict.settings.HeadlightPanels.Configuration.NavBar.Brand.buttonClass~}" 
						name="{~D:Pict.settings.HeadlightPanels.Configuration.NavBar.Brand.name~}" 
						onclick="{~D:Pict.settings.HeadlightPanels.Configuration.NavBar.Brand.onclick~}"
					>
						<span class="icon {~Hide:Pict.settings.HeadlightPanels.Configuration.NavBar.Brand.hideIcon~}">
							<i id="HeadlightPanels-NavBar-Brand-Icon" class="fas fa-fw {~D:Pict.settings.HeadlightPanels.Configuration.NavBar.Brand.iconClass~}"></i>
						</span>
						<span id="HeadlightPanels-NavBar-Brand-Text" title="{~DocumentState:AppData.DocumentData.ReportData~}">{~DocumentState:AppData.DocumentData.ReportData~}</span>
					</button>

				</div>`,
			},
			{
				Hash: 'HeadlightPanels-NavBar-Button-Wrapper',
				Template: /*html*/`{~TS:HeadlightPanels-NavBar-Button:Pict.settings.HeadlightPanels.Configuration.NavBar.Buttons~}`,
			},
			{
				Hash: 'HeadlightPanels-NavBar-Button',
				Template: /*html*/`
				<div class="navbar-item {~D:Record.wrapperClass~}">
					{~NE:Record.showAutoSaveStatus^<span id="HeadlightPanels-Navbar-AutoSaveStatus-Container" class="hl-autosave-status-container"></span>~}
					<button id="{~D:Record.id~}" type="button" class="button {~D:Record.buttonClass~}" name="{~D:Record.name~}" onclick="{~D:Record.onclick~}">
						<span class="icon {~Hide:Record.hideIcon~}">
							<i id="{~D:Record.id~}-Icon" class="fas fa-fw {~D:Record.iconClass~}"></i>
						</span>
						{~If:Record.displayName~}
					</button>
				</div>`,
			},
			{
				// Auto-save indicator. Deliberately not a toast: a notification every 90 seconds trains
				// users to dismiss notifications. StatusSlot is empty whenever auto-save is off or the
				// document is not editable, which renders nothing at all.
				Hash: 'HeadlightPanels-NavBar-AutoSaveStatus',
				Template: /*html*/`{~TS:HeadlightPanels-NavBar-AutoSaveStatus-Content:AppData.AutoSave.StatusSlot~}`,
			},
			{
				Hash: 'HeadlightPanels-NavBar-AutoSaveStatus-Content',
				Template: /*html*/`
				<span class="hl-autosave-status {~D:Record.StateClass~} {~D:Record.FlashClass~}" role="status" aria-live="polite" title="{~D:Record.Tooltip~}">
					<span class="icon is-small">
						<i class="fas fa-fw {~D:Record.IconClass~}" aria-hidden="true"></i>
					</span>
					<span class="hl-autosave-status-label is-hidden-mobile">{~D:Record.Label~}</span>
				</span>`,
			},
			{
				Hash: 'HeadlightPanels-Header-ButtonGroup',
				Template: /*html*/`
				<button type="button" class="is-pulled-right hl-panel-close-button" aria-label="close" title="Close panel" onclick="_Pict.views.HeadlightPanels.closeThisPanel(event)">
					<span class="icon">
						<i class="fas fa-fw fa-times-circle" aria-hidden="true"></i>
					</span>
				</button>
				<button type="button" class="is-pulled-right hl-panel-pin-button mr-2" aria-label="pin" title="Pin content" onclick="_Pict.views.HeadlightPanels.togglePanelPinState()">
					<span class="icon">
						<i class="fas fa-fw fa-thumbtack" aria-hidden="true"></i>
					</span>
				</button>`,
			},
			{
				Hash: 'HeadlightPanels-Header-ModeBanner-Observations',
				Template: /*html*/`
				<div data-panel-mode-show-if="add-to-set" class="notification is-radiusless add-mode-banner px-4 py-2">
					<div class="add-mode-banner-content mr-5">
						<span class="icon">
							<i class="fa fa-fw fa-info-circle"></i>
						</span>
						<strong class="add-mode-message">Adding Observations to <span id="HeadlightPanels-ObservationPanel-Observation-Header-Mode-Target" class="add-mode-title">{~T:HeadlightPanels-ObservationPanel-BannerTarget~}</span></strong>
					</div>
				</div>`,
			},
			{
				Hash: 'HeadlightPanels-Header-ModeBanner-Samples',
				Template: /*html*/`
				<div data-panel-mode-show-if="add-to-set" class="notification is-radiusless add-mode-banner px-4 py-2">
					<div class="add-mode-banner-content mr-5">
						<span class="icon">
							<i class="fa fa-fw fa-info-circle"></i>
						</span>
						<strong class="add-mode-message">Adding Samples to <span id="HeadlightPanels-SamplePanel-Sample-Header-Mode-Target" class="add-mode-title">Sample Selector</strong>
					</div>
					<button type="button" class="delete" onclick="_Pict.views.HeadlightPaneSamples.clearSamplePanelTarget()"></button>
				</div>`,
			},
			{
				Hash: 'HeadlightPanels-Header-ModeBanner-Documents',
				Template: /*html*/`
				<div data-panel-mode-show-if="add-to-inspection" class="notification is-radiusless add-mode-banner px-4 py-2">
					<div class="add-mode-banner-content mr-5">
						<span class="icon">
							<i class="fa fa-fw fa-info-circle"></i>
						</span>
						<strong class="add-mode-message">Adding Documents to <span id="HeadlightPanels-DocumentPanel-Document-Header-Mode-Target" class="add-mode-title">{~T:HeadlightPanels-DocumentPanel-BannerTarget~}</span></strong>
					</div>
				</div>`,
			},
			{
				// This is a generic modal container to be used for any full screen modal content by other views as needed.
				// Treat this as the ONE modal to rule them all.
				Hash: 'HeadlightPanels-FullScreenModal',
				Template: /*html*/`
				<div id="HeadlightPanels-FullScreenModal" class="modal is-active">
					<div class="modal-background"></div>
					<div class="modal-content">
						<div class="box">
							<p>
								<span class="icon">
									<i class="fa fa-fw fa-circle-question"></i>
								</span>
								<span>No content has been set here yet.</span>
							</p>
						</div>
					</div>
					<button class="modal-close is-large" aria-label="close" onclick="_Pict.views.HeadlightPanels.togglePanelFullScreenModal('close')"></button>
				</div>`,
			},
			{
				Hash: 'HeadlightPanels-InDevelopmentMessage',
				Template: /*html*/`
				<div class="notification is-warning is-soft has-text-centered m-4">
					<span class="icon">
						<i class="fas fa-fw fa-tools has-text-warning" aria-hidden="true"></i>
					</span>
					<span class="icon-text">This feature is still in development and is not fully available at this time.</span>
				</div>`,
			},
		],
	Renderables:
		[
			{
				RenderableHash: 'HeadlightPanels',
				TemplateHash: 'HeadlightPanels-Template',
				ContentDestinationAddress: '#HeadlightPanels-Container',
			},
			{
				RenderableHash: 'HeadlightPanels-NavBar-Brand',
				TemplateHash: 'HeadlightPanels-NavBar-Brand',
				ContentDestinationAddress: '#HeadlightPanels-Navbar-Brand-Container',
			},
			{
				RenderableHash: 'HeadlightPanels-NavBar-Button-Wrapper',
				TemplateHash: 'HeadlightPanels-NavBar-Button-Wrapper',
				ContentDestinationAddress: '#HeadlightPanels-Navbar-Button-Container',
			},
			{
				RenderableHash: 'HeadlightPanels-ObservationPanel',
				TemplateHash: 'HeadlightPanels-ObservationPanel',
				ContentDestinationAddress: '#HeadlightPanels-ObservationPanel',
			},
			{
				RenderableHash: 'HeadlightPanels-ObservationPanel-Included',
				TemplateHash: 'HeadlightPanels-ObservationPanel-Included',
				ContentDestinationAddress: '#HeadlightPanels-ObservationPanel-Included-Content',
			},
			{
				RenderableHash: 'HeadlightPanels-ObservationPanel-Detail',
				TemplateHash: 'HeadlightPanels-ObservationPanel-Detail',
				ContentDestinationAddress: '#HeadlightPanels-ObservationPanel-Detail-Content',
			},
			{
				RenderableHash: 'HeadlightPanels-FullScreenModal',
				TemplateHash: 'HeadlightPanels-FullScreenModal',
				ContentDestinationAddress: '#HeadlightPanels-FullScreenModal-Container',
			},
			{
				RenderableHash: 'ObservationDetails-FullScreenModal',
				TemplateHash: 'ObservationDetails-FullScreenModal',
				DestinationAddress: '#HeadlightPanels-FullScreenModal-Container',
				RenderType: 'replace',
			},
		],
});
